/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2023] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backbone from 'backbone';

import StringCell from '@/backgrid/StringCell';
import GetAllRegions from '@/services/GetAllRegions';
import GetAllTrainingTypes from '@/services/GetAllTrainingTypes';
import GetAdditionalItemsByOrderFormTypeID from '@/services/GetAdditionalItemsByOrderFormTypeID';
import GetMFDAdditionalServicesByObjectID from '@/services/GetMFDAdditionalServicesByObjectID';
import DeliveryScheduleGridView from '@/views/DeliveryScheduleGridView';
import SpecialtyRequirementDeliveryScheduleGridView from '@/views/SpecialtyRequirementDeliveryScheduleGridView';
import A3DeliveryScheduleGridView from '@/views/A3DeliveryScheduleGridView';
import OrderTypes from '@/enums/OrderTypes';
import GetDeliveryScheduleColumnsByUserAndOrderType from '@/services/GetDeliveryScheduleColumnsByUserAndOrderType';

import AWPackageAccess from '../utils/AWPackageAccess';
import GetCONUSAndOCONUSRegions from '../../order/services/GetCONUSAndOCONUSRegions';
import GetAWPackageCustomerOrderDeliverySchedules from '../services/GetAWPackageCustomerOrderDeliverySchedules';
import GetAWPackage from '../services/GetAWPackage';

export default async function loadAWPackageDeliveryScheduleGridView({ awPackageId, editable }) {
  const awPackage = await GetAWPackage(awPackageId);
  let additionalItemsService, data1, data2;
  if (awPackage.orderFormTypeId === '1' || awPackage.orderFormTypeId === '3') {
    //MFD
    additionalItemsService = GetMFDAdditionalServicesByObjectID;
    data1 = awPackageId;
    data2 = 'AW PACKAGE';
  } else {
    additionalItemsService = GetAdditionalItemsByOrderFormTypeID;
    data1 = awPackage.orderFormTypeId;
  }

  const [regions, schedules, trainingTypes, additionalItems, conusOconusRegions, prtnrA3Columns] = await Promise.all([
    GetAllRegions(),
    GetAWPackageCustomerOrderDeliverySchedules(awPackageId),
    GetAllTrainingTypes(),
    additionalItemsService(data1, data2),
    GetCONUSAndOCONUSRegions(),
    GetDeliveryScheduleColumnsByUserAndOrderType(awPackage.orderFormTypeId)
  ]);

  let columns = [];
  let isDownloadable, isRenderable;

  _.each(prtnrA3Columns, (col) => {
    isRenderable = AWPackageAccess.isDeliveryScheduleColumnRenderable(col.renderableBy);
    isDownloadable = AWPackageAccess.isDeliveryScheduleColumnDownloadable(col.downloadableBy);

    columns.push({
      name: col.name,
      label: col.label,
      cell: StringCell,
      headerCell: 'custom',
      width: parseInt(col.width),
      editable: false,
      downloadable: isDownloadable,
      renderable: isRenderable
    });
  });

  const isReplacementOptions = [
    { id: 0, name: 'New' },
    { id: 1, name: 'Replacement' }
  ];

  const isMFD = awPackage.orderFormTypeId === '1' || awPackage.orderFormTypeId === '3' ? true : false;
  const isSpecialtyRequirement = awPackage.orderFormTypeId === OrderTypes.SPECIALTY_REQ;
  const isPRTNRA3 = awPackage.orderFormTypeId === OrderTypes.PRTNR_A3;

  const sortedSchedules = _.sortBy(schedules, (schedule) => {
    return schedule.clin;
  });
  _.each(sortedSchedules, (schedule, i) => {
    let colorText, niprText;
    if (schedule.isColor === '1') {
      colorText = 'C';
    } else if (schedule.isColor === '0') {
      colorText = 'M';
    }
    if (schedule.isNipr === '1') {
      niprText = 'NIPR';
    } else if (schedule.isNipr === '0') {
      niprText = 'SIPR';
    }

    schedule.device = {
      model: schedule.model,
      laserPrinterTonerCartridgeType: schedule.laserPrinterTonerCartridgeType,
      configurationAndPricing: schedule.configurationAndPricing,
      description: schedule.description,
      volumeBand: schedule.volumeBand,
      colorText,
      niprText,
      optionalAccessories: schedule.optionalAccessories,
      equipmentName: schedule.equipmentName
    };

    schedule.isReplacementText =
      schedule.isReplacement === '0' ? 'New' : schedule.isReplacement === '1' ? 'Replacement' : '';
    schedule.trainingTypeText = schedule.trainingType;
    schedule.lineClin = schedule.lineCLIN;
    schedule.additionalServiceText = schedule.additionalService
      ? schedule.additionalService
      : schedule.mfdAdditionalService;
    schedule.additionalAccessoryText = schedule.additionalAccessory ? schedule.additionalAccessory : null;
    schedule.colorText = colorText;
    schedule.niprText = niprText;
    schedule.formattedPrice = parseInt(schedule.price).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

    setAddress(schedule);
    setEquipmentName(schedule, awPackage.orderFormTypeId);
  });

  const itPOCExists = setItPocExists(awPackage);

  if (isSpecialtyRequirement) {
    return new SpecialtyRequirementDeliveryScheduleGridView({
      objectTypeCode: 'AW_PACKAGE',
      objectID: awPackageId,
      itPOCExists,
      editable,
      isPriceVisible: true,
      model: new Backbone.Model({
        regions: regions?.filter((region) => region?.isSpecialty === 'false'),
        schedules,
        awPackage,
        trainingTypes,
        isReplacementOptions,
        additionalServices: additionalItems.additionalServices,
        isMFD,
        conusOconusRegions
      })
    });
  } else if (isPRTNRA3) {
    return new A3DeliveryScheduleGridView({
      objectTypeCode: 'AW_PACKAGE',
      objectID: awPackageId,
      itPOCExists: true,
      finPOCExists: true,
      editable,
      model: new Backbone.Model({
        regions: regions?.filter((region) => region?.isSpecialty === 'false'),
        columns,
        schedules: sortedSchedules,
        awPackage,
        trainingTypes,
        isReplacementOptions,
        additionalServices: additionalItems.additionalServices,
        additionalAccessories: additionalItems.additionalAccessories,
        isMFD,
        conusOconusRegions
      })
    });
  } else {
    return new DeliveryScheduleGridView({
      objectTypeCode: 'AW_PACKAGE',
      objectID: awPackageId,
      itPOCExists,
      editable,
      model: new Backbone.Model({
        regions: regions?.filter((region) => region?.isSpecialty === 'false'),
        schedules: sortedSchedules,
        awPackage,
        trainingTypes,
        isReplacementOptions,
        additionalServices: additionalItems.additionalServices,
        additionalAccessories: additionalItems.additionalAccessories,
        isMFD,
        conusOconusRegions
      })
    });
  }
}

function setAddress(schedule) {
  schedule.addressLine1 = schedule.address.addressLine1;
  schedule.addressLine2 = schedule.address.addressLine2;
  schedule.streetAddress = schedule?.addressLine1?.concat(', ', schedule?.addressLine2);
  schedule.buildingFloorNumber = schedule.address.buildingFloorNumber;
  schedule.floorNumber = schedule.address.floorNumber;
  schedule.roomNumber = schedule.address.roomNumber;
  schedule.poleNumber = schedule.address.poleNumber;
  schedule.suite = schedule.address.suite;
  schedule.roomPoleNumber = schedule.address.roomPoleNumber;
  schedule.city = schedule.address.city;
  schedule.regionCode = schedule.address.regionCode;
  schedule.zip = schedule.address.zip;
  if (schedule.address.zip) {
    schedule.address.isConus = '1';
  }
}

function setEquipmentName(schedule, orderFormTypeID) {
  if (orderFormTypeID === OrderTypes.PRTNR_A3) {
    schedule.equipmentName = schedule.device.equipmentName;
    return;
  }

  if (schedule.device.model) {
    schedule.equipmentName = schedule.device.model;
  } else if (schedule.device.laserPrinterTonerCartridgeType) {
    schedule.equipmentName = schedule.device.laserPrinterTonerCartridgeType;
  } else if (schedule.device.configurationAndPricing) {
    schedule.equipmentName = schedule.device.volumeBand.concat('  -  ', schedule.device.configurationAndPricing);
  } else if (schedule.device.description) {
    schedule.equipmentName = schedule.device.description;
  } else if (schedule.device.volumeBand) {
    schedule.equipmentName = schedule.device.volumeBand.concat(schedule.device.niprText, schedule.device.colorText);
    if (schedule.device.optionalAccessories) {
      schedule.equipmentName = schedule.equipmentName.concat('  -  ', schedule.device.optionalAccessories);
    }
  } else {
    schedule.equipmentName = '';
  }
}

function setItPocExists(awPackage) {
  return (
    awPackage.orderFormTypeId === OrderTypes.PRTNR ||
    awPackage.orderFormTypeId === OrderTypes.MFD ||
    awPackage.orderFormTypeId === OrderTypes.JAPAN
  );
}
